import React, { useEffect, useState, useRef } from "react";
import {useSelector} from "react-redux";
import moment from "moment";
import { t } from "i18next";
import PromotionalBanner from "features/promotional-banner";
import { CustomModal } from "components";
import { Close } from "icons";

const rushHourSchedule = {
  sunday: [
    [10.5, 15],
    [19, 23],
  ],
  monday: [
    [12, 14],
    [19, 22],
  ],
  tuesday: [
    [12, 14],
    [19, 22],
  ],
  wednesday: [
    [12, 20],
    [19, 22],
  ],
  thursday: [
    [12, 14],
    [19, 22],
  ],
  friday: [
    [12, 14],
    [13.5, 15],
    [19, 22],
  ],
  saturday: [
    [10.5, 15],
    [19, 23],
  ],
};

const excludedBrandIds = ["69021633d1476f5d6de1a690"];

// check rush hour
const isRushHourNow = () => {
  const now = moment();

  // Eid override: 5–8 June 2025 inclusive
  const eidStart = moment("2025-06-05").startOf("day");
  const eidEnd = moment("2025-06-08").endOf("day");
  if (now.isBetween(eidStart, eidEnd, null, "[]")) {
    return true; // whole day is rush hour
  }

  const dayName = now.format("dddd").toLowerCase();
  const todaySchedule = rushHourSchedule[dayName] || [];

  return todaySchedule.some(([start, end]) => {
    // Convert decimal hours (e.g. 16.25) into moment objects
    const startMoment = moment()
      .startOf("day")
      .add(start * 60, "minutes");
    const endMoment = moment()
      .startOf("day")
      .add(end * 60, "minutes");

    return now.isBetween(startMoment, endMoment, null, "[]");
  });
};

// ms until next boundary
const getMsUntilNextBoundary = () => {
  const now = moment();
  const dayName = now.format("dddd").toLowerCase();
  const todaySchedule = rushHourSchedule[dayName] || [];

  // Build today's start & end times as moment objects
  const boundaries = todaySchedule.flatMap(([start, end]) => [
    moment()
      .startOf("day")
      .add(start * 60, "minutes"),
    moment()
      .startOf("day")
      .add(end * 60, "minutes"),
  ]);

  // Find the first boundary that’s still in the future
  const nextBoundary = boundaries.find((boundary) => boundary.isAfter(now));

  // If no boundary left today → next check is midnight tomorrow
  const fallback = moment().add(1, "day").startOf("day");

  return (nextBoundary || fallback).diff(now);
};


const RenderRushHourPopup = ({ asModal = false, handleClose = () => {} }) => {
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  if(excludedBrandIds.includes(brandInfo?.brand?._id)) {
    return null;
  }
  return (
    <div className="relative w-full" style={{ top: asModal ? "-5px" : "0px" }}>
      {/* Top triangular pattern */}
      <div className="w-full overflow-hidden flex">
        {[...Array(30)].map((_, i) => (
          <div
            key={`top-${i}`}
            className="w-8 h-4 bg-transparent"
            style={{
              borderBottom: "8px solid #FEE2E2",
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="bg-red-100 p-6 relative">
        <button
          className="absolute top-0 rush-hour-close-button z-10 group cursor-pointer"
          onClick={handleClose}
        >
          <Close className="text-red-700 group-hover:text-red-500 group-hover:scale-150 transition-all duration-300" />
        </button>
        <p className="text-16x text-red-800">
          <span className="font-bold">ⓘ</span>{" "}
          {t("It's taking longer than usual to deliver due to high demand")}
        </p>
      </div>

      {/* Bottom triangular pattern */}
      <div className="w-full overflow-hidden flex">
        {[...Array(30)].map((_, i) => (
          <div
            key={`bottom-${i}`}
            className="w-8 h-4 bg-transparent"
            style={{
              borderTop: "8px solid #FEE2E2",
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
            }}
          />
        ))}
      </div>
    </div>
  )
}

const RushHourPopup = ({ asModal = false }) => {
  moment.locale("en");
  const [isRushHour, setIsRushHour] = useState(isRushHourNow());
  const [dismissed, setDismissed] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const scheduleNextCheck = () => {
      const isRushHour = isRushHourNow();
      setIsRushHour(isRushHour);
      setDismissed(false); // reset dismissal at each boundary

      const delay = getMsUntilNextBoundary();
      timeoutRef.current = setTimeout(scheduleNextCheck, delay);
    };

    scheduleNextCheck();
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const handleClose = () => setDismissed(true);

  return (
    <>
      {window.location.pathname?.includes("checkout") ? null : <PromotionalBanner />}
      {
      (!isRushHour || dismissed) ? null : (   
      asModal ? (
        <CustomModal
          open={isRushHour && !dismissed}
          setOpen={handleClose}
          bodyclasses="flex"XW
          overlayClasses="fixed w-screen top-0 overflow-hidden transition ease-linear duration-700"
        >
          <RenderRushHourPopup asModal={asModal} handleClose={handleClose} />
        </CustomModal>
      ) : (
        <RenderRushHourPopup asModal={asModal} handleClose={handleClose} />
      ))}
    </>
  );
};

export default RushHourPopup;
